// web/src/screens/admin/prompt_vars.ts — migration 0034.
//
// Shared catalogue of substitution variables surfaced under the
// llm_user_prompt textareas across the admin screens (Settings, Game,
// Card, Task). Keep this array in sync with internal/llm/prompts_subst.go
// (SubstUserPromptTokens). The Go code is the wire contract; this file
// is the authoring-UI mirror.
//
// The system prompt is sent verbatim — no token expansion — so there is
// no SYSTEM_PROMPT_VARS list.

// User-prompt substitution tokens (11-token contract — 9 from migration
// 0034 plus the 2 entity-description tokens from migration 0036).
export const USER_PROMPT_VARS: ReadonlyArray<readonly [string, string]> = [
  ["{{PlayerName}}", "the player's name"],
  ["{{PlayerAge}}", "the player's age in years (blank if unknown)"],
  ["{{PlayerSex}}", 'the player’s sex in Latvian — "zēns", "meitene", or "cits" (blank if unknown)'],
  ["{{ExpectedAnswer}}", "the LV-resolved correct answer for this task"],
  ["{{AcceptedAlternativeAnswers}}", "comma-separated alternative answers (empty if none)"],
  ["{{LastTextSpokenToKid}}", "the LV text last spoken to the child this turn (task prompt or engine retry/cue)"],
  ["{{AttemptNumber}}", "1-based attempt number on the current task this turn"],
  ["{{MaxAttempts}}", "max attempts allowed on this task (task.max_tries or game.default_max_tries)"],
  ["{{ChildTranscript}}", "the child's STT transcript — bare string for single source, structured 'Avots N — …' block when multi-source"],
  ["{{GameDescription}}", "operator-authored description of the current game (Game → Description; blank if unset)"],
  ["{{TaskInstructions}}", "LV instructions for the current task — Task → Task instruction (first member) if set, else Game → Default task instruction; blank if neither set"],
];

// promptVarsInfoBox renders a small panel below a prompt textarea that
// lists the recognised tokens. `vars` is either SYSTEM_PROMPT_VARS or
// USER_PROMPT_VARS; `label` is the heading text. All text is set via
// textContent / createTextNode — never raw HTML (T-11-09a).
export function promptVarsInfoBox(
  vars: ReadonlyArray<readonly [string, string]>,
  label = "Substitution variables (expanded per turn):",
): HTMLElement {
  const box = document.createElement("div");
  box.setAttribute("style", "margin-top: 4px; padding: 8px 10px; background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 12px; color: #4b5563; line-height: 1.5;");
  const title = document.createElement("div");
  title.textContent = label;
  title.setAttribute("style", "font-weight: 500; margin-bottom: 4px;");
  box.appendChild(title);
  const ul = document.createElement("ul");
  ul.setAttribute("style", "margin: 0; padding-left: 18px;");
  for (const [token, desc] of vars) {
    const li = document.createElement("li");
    const code = document.createElement("code");
    code.textContent = token;
    code.setAttribute("style", "background: #e5e7eb; padding: 0 3px; border-radius: 3px;");
    li.appendChild(code);
    li.appendChild(document.createTextNode(` — ${desc}`));
    ul.appendChild(li);
  }
  box.appendChild(ul);
  return box;
}

// makePromptTextarea is a small helper to keep all 4 admin screens
// rendering the prompt textareas with identical styling. `rows` defaults
// to 6 — taller than the standard makeTextarea so an operator can see a
// few-line template comfortably without resizing.
export function makePromptTextarea(value?: string | null, rows = 6): HTMLTextAreaElement {
  const el = document.createElement("textarea");
  el.value = value ?? "";
  el.rows = rows;
  el.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; font-family: ui-monospace, monospace; resize: vertical;");
  return el;
}

// OverridePromptHandle is the public surface of an "Override [...] prompt"
// widget — a checkbox that toggles a hidden textarea + the vars info box.
// value() returns "" when the checkbox is unchecked (cleared → inherit on
// PATCH) and the textarea's text when checked (explicit override).
// setValue() resets the widget to a loaded API value: empty/undefined
// leaves the checkbox off, non-empty turns it on with the loaded text.
export interface OverridePromptHandle {
  container: HTMLElement;
  textarea: HTMLTextAreaElement;
  setValue(stored: string | null | undefined): void;
  value(): string;
}

// mountOverridePromptField builds the Override-checkbox + hidden-textarea
// widget used by Game (system + user prompt), Card (user prompt) and Task
// (user prompt) edit forms. The widget mirrors the cascade semantics: the
// textarea is hidden by default and a "" value on the wire means "clear
// this override / inherit from the parent" (cascade walks task → card →
// game → config for user prompt; game → config for system prompt).
//
// `labelText` is the checkbox label (e.g. "Override LLM user prompt
// (inherit from Game/Settings when off)"). `varsBox` is the
// promptVarsInfoBox() result; the helper appends it under the textarea
// so the substitution-vars panel is only visible when the override is
// engaged. Pass `null` for prompts that don't support substitution
// (system prompt). `rows` matches makePromptTextarea's default.
export function mountOverridePromptField(
  labelText: string,
  varsBox: HTMLElement | null,
  rows = 6,
): OverridePromptHandle {
  const container = document.createElement("div");
  container.setAttribute("style", "display: flex; flex-direction: column; gap: 6px;");

  const cbLabel = document.createElement("label");
  cbLabel.setAttribute("style", "display: flex; align-items: center; gap: 8px; font-size: 13px; color: #374151; font-weight: 500; cursor: pointer;");
  const checkbox = document.createElement("input");
  checkbox.type = "checkbox";
  const cbText = document.createElement("span");
  cbText.textContent = labelText;
  cbLabel.append(checkbox, cbText);
  container.appendChild(cbLabel);

  const inner = document.createElement("div");
  inner.setAttribute("style", "display: none; flex-direction: column; gap: 4px;");
  const textarea = makePromptTextarea("", rows);
  inner.appendChild(textarea);
  if (varsBox) inner.appendChild(varsBox);
  container.appendChild(inner);

  function applyVisibility(): void {
    inner.style.display = checkbox.checked ? "flex" : "none";
  }
  checkbox.addEventListener("change", applyVisibility);

  return {
    container,
    textarea,
    setValue(stored): void {
      const v = stored ?? "";
      checkbox.checked = v !== "";
      textarea.value = v;
      applyVisibility();
    },
    value(): string {
      return checkbox.checked ? textarea.value : "";
    },
  };
}
